import React, { useState } from 'react';
import {
  Shield,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Music,
  FileText,
  Eye,
  Volume2,
  Subtitles,
  AlertCircle,
} from 'lucide-react';
import { Badge } from '../ui/badge';
import Card from '../ui/CardComponent';
import Button from '../ui/ButtonComponent';

export default function ComplianceCheck() {
  const [ageRestriction, setAgeRestriction] = useState('all-audiences');
  const [scanningStatus,] = useState<'scanning' | 'complete'>('complete');
  const [subtitlesEnabled, setSubtitlesEnabled] = useState(false);

  const [contentSensitivity, setContentSensitivity] = useState({
    mildLanguage: 'none',
    strongLanguage: 'none',
    violence: 'none',
    blood: 'none',
    adultThemes: 'none',
    dangerousActs: 'none',
    drugAlcohol: 'none',
    hate: 'none',
    political: 'none',
    shocking: 'none',
  });

  const detectedAudio = {
    name: 'Summer Vibes Mix 2024',
    artist: 'DJ Beats',
    duration: '3:24',
    status: 'licensed',
    detectedAt: '2:15',
  };

  const copyrightScanResults = {
    audio: { status: 'pass', message: 'No issues found' },
    visual: { status: 'pass', message: 'No issues found' },
    text: { status: 'warning', message: 'Brand mention detected' },
  };

  const handleSensitivityChange = (key: string, value: string) => {
    setContentSensitivity((prev) => ({ ...prev, [key]: value }));
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'pass':
      case 'licensed':
        return <CheckCircle size={20} className="text-green-600" />;
      case 'warning':
        return <AlertTriangle size={20} className="text-yellow-600" />;
      case 'fail':
      case 'unlicensed':
        return <XCircle size={20} className="text-red-600" />;
      default:
        return <AlertCircle size={20} className="text-gray-400" />;
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'pass':
      case 'licensed':
        return <Badge variant="secondary">✓ Licensed</Badge>;
      case 'warning':
        return <Badge variant="destructive">⚠ Needs Review</Badge>;
      case 'fail':
      case 'unlicensed':
        return <Badge variant="default">✕ Unlicensed</Badge>;
      default:
        return <Badge variant="default">Unknown</Badge>;
    }
  };

  const handleFixIssues = () => {
    const issues = [];
    if (copyrightScanResults.text.status === 'warning') {
      issues.push('Brand mention detected in text');
    }
    if (!subtitlesEnabled) {
      issues.push('Subtitles not enabled');
    }
    if (Object.values(contentSensitivity).some((v) => v !== 'none')) {
      issues.push('Sensitive content flagged');
    }

    if (issues.length > 0) {
      alert('Issues to fix:\n\n' + issues.map((issue, i) => `${i + 1}. ${issue}`).join('\n'));
    } else {
      alert('No issues found! Your content is ready to schedule.');
    }
  };

  const handleContinueToSchedule = () => {
    // Validation checks
    const warnings = [];

    if (copyrightScanResults.text.status === 'warning') {
      warnings.push('You have unresolved copyright warnings');
    }
    if (!subtitlesEnabled) {
      warnings.push('Subtitles are not enabled (recommended for accessibility)');
    }

    if (warnings.length > 0) {
      // eslint-disable-next-line no-restricted-globals
      const proceed = confirm(
        'The following warnings were detected:\n\n' +
          warnings.map((w, i) => `${i + 1}. ${w}`).join('\n') +
          '\n\nDo you want to continue anyway?'
      );
      if (!proceed) return;
    }

    alert('✓ Compliance check passed! Redirecting to scheduler...');
    // In a real app, this would navigate to the scheduler page
    // window.location.href = '/calendar';
  };

  const handleVerifyLicense = () => {
    alert(
      'Verifying audio license...\n\nThis would open a dialog to manually verify or update the license information for the detected audio track.'
    );
  };

  return (
    <div className="max-w-[1600px] mx-auto">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-[#F9FAFB] border border-[#E5E7EB] rounded flex items-center justify-center">
              <Shield size={20} className="text-[#6B7280]" />
            </div>
            <h1 className="text-2xl font-semibold text-[#111827]">Content Compliance & Safety</h1>
          </div>
          <div className="flex items-center gap-3">
            <Button variant="secondary" size="md" onClick={handleFixIssues}>
              Fix Issues
            </Button>
            <Button variant="primary" size="md" onClick={handleContinueToSchedule}>
              Continue to Schedule
            </Button>
          </div>
        </div>
        <p className="text-sm text-[#6B7280]">Ensure your content meets platform guidelines and safety requirements</p>
      </div>

      {/* Main Content Grid */}
      <div className="space-y-8">
        {/* A. Age Restriction Panel */}
        <Card padding="none">
          <div className="p-6 border-b border-[#E5E7EB]">
            <h2 className="text-lg font-semibold text-[#111827] mb-1">Audience / Age Classification</h2>
            <p className="text-sm text-[#6B7280]">Select the appropriate audience for this content</p>
          </div>
          <div className="p-6">
            <div className="space-y-4">
              {/* Option 1 */}
              <label className="flex items-start gap-4 p-4 border border-[#E5E7EB] rounded-lg cursor-pointer hover:border-[#9CA3AF] transition-colors">
                <input
                  type="radio"
                  name="age-restriction"
                  value="all-audiences"
                  checked={ageRestriction === 'all-audiences'}
                  onChange={(e) => setAgeRestriction(e.target.value)}
                  className="mt-1"
                />
                <div className="flex-1">
                  <div className="font-medium text-[#111827] mb-1">Suitable for all audiences</div>
                  <div className="text-sm text-[#6B7280]">
                    Content is appropriate for viewers of all ages, including children and families.
                  </div>
                </div>
              </label>

              {/* Option 2 */}
              <label className="flex items-start gap-4 p-4 border border-[#E5E7EB] rounded-lg cursor-pointer hover:border-[#9CA3AF] transition-colors">
                <input
                  type="radio"
                  name="age-restriction"
                  value="not-for-kids"
                  checked={ageRestriction === 'not-for-kids'}
                  onChange={(e) => setAgeRestriction(e.target.value)}
                  className="mt-1"
                />
                <div className="flex-1">
                  <div className="font-medium text-[#111827] mb-1">Not made for kids</div>
                  <div className="text-sm text-[#6B7280]">
                    Content is intended for general audiences but may not be suitable for young children.
                  </div>
                </div>
              </label>

              {/* Option 3 */}
              <label className="flex items-start gap-4 p-4 border border-[#E5E7EB] rounded-lg cursor-pointer hover:border-[#9CA3AF] transition-colors">
                <input
                  type="radio"
                  name="age-restriction"
                  value="18-plus"
                  checked={ageRestriction === '18-plus'}
                  onChange={(e) => setAgeRestriction(e.target.value)}
                  className="mt-1"
                />
                <div className="flex-1">
                  <div className="font-medium text-[#111827] mb-1">18+ restricted content</div>
                  <div className="text-sm text-[#6B7280]">
                    Content contains adult themes and is only suitable for viewers 18 years and older.
                  </div>
                </div>
              </label>

              {/* Option 4 */}
              <label className="flex items-start gap-4 p-4 border border-[#E5E7EB] rounded-lg cursor-pointer hover:border-[#9CA3AF] transition-colors">
                <input
                  type="radio"
                  name="age-restriction"
                  value="sensitive"
                  checked={ageRestriction === 'sensitive'}
                  onChange={(e) => setAgeRestriction(e.target.value)}
                  className="mt-1"
                />
                <div className="flex-1">
                  <div className="font-medium text-[#111827] mb-1">Contains sensitive or mature themes</div>
                  <div className="text-sm text-[#6B7280]">
                    Content may include sensitive topics that require viewer discretion and maturity.
                  </div>
                </div>
              </label>
            </div>
          </div>
        </Card>

        {/* B. Audio License Verification */}
        <Card padding="none">
          <div className="p-6 border-b border-[#E5E7EB]">
            <h2 className="text-lg font-semibold text-[#111827] mb-1">Audio Licensing</h2>
            <p className="text-sm text-[#6B7280]">Verify copyright status of detected audio content</p>
          </div>
          <div className="p-6">
            <div className="border border-[#E5E7EB] rounded-lg p-4 bg-[#F9FAFB]">
              <div className="flex items-center gap-4">
                {/* Waveform Preview */}
                <div className="w-16 h-16 bg-white border border-[#E5E7EB] rounded flex items-center justify-center flex-shrink-0">
                  <Volume2 size={24} className="text-[#6B7280]" />
                </div>

                {/* Audio Details */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-[#111827] mb-1">{detectedAudio.name}</div>
                      <div className="text-sm text-[#6B7280]">
                        {detectedAudio.artist} • Duration: {detectedAudio.duration} • Detected at{' '}
                        {detectedAudio.detectedAt}
                      </div>
                    </div>
                    <div className="ml-4 flex-shrink-0">{getStatusBadge(detectedAudio.status)}</div>
                  </div>
                  <div className="text-xs text-[#9CA3AF] mb-3">Detected automatically</div>
                  <Button variant="secondary" size="sm" onClick={handleVerifyLicense}>
                    Verify License
                  </Button>
                </div>
              </div>
            </div>

            {/* Additional Audio Info */}
            <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded text-sm text-blue-900">
              <div className="flex items-start gap-2">
                <AlertCircle size={16} className="text-blue-600 flex-shrink-0 mt-0.5" />
                <div>
                  <strong>Platform Notice:</strong> Instagram Reels and TikTok may restrict copyrighted audio. Consider
                  using royalty-free alternatives from our Media Library.
                </div>
              </div>
            </div>
          </div>
        </Card>

        {/* C. Content Sensitivity Self-Certification */}
        <Card padding="none">
          <div className="p-6 border-b border-[#E5E7EB]">
            <h2 className="text-lg font-semibold text-[#111827] mb-1">Content Sensitivity Self-Certification</h2>
            <p className="text-sm text-[#6B7280]">Classify sensitive content to ensure proper audience targeting</p>
          </div>
          <div className="p-6">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-[#1F2937] text-white text-left text-xs font-medium uppercase tracking-wider">
                    <th className="px-6 py-3">Content Type</th>
                    <th className="px-6 py-3 text-right">Severity Level</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-[#E5E7EB]">
                  {[
                    { key: 'mildLanguage', label: 'Mild language / profanity' },
                    { key: 'strongLanguage', label: 'Strong language' },
                    { key: 'violence', label: 'Violence or injury' },
                    { key: 'blood', label: 'Depictions of blood' },
                    { key: 'adultThemes', label: 'Adult themes / sexual references' },
                    { key: 'dangerousActs', label: 'Harmful or dangerous acts' },
                    { key: 'drugAlcohol', label: 'Drug / alcohol references' },
                    { key: 'hate', label: 'Hate / abusive expressions' },
                    { key: 'political', label: 'Political / election-related content' },
                    { key: 'shocking', label: 'Shocking or disturbing visuals' },
                  ].map((item) => (
                    <tr key={item.key} className="hover:bg-[#F9FAFB] transition-colors">
                      <td className="px-6 py-4">
                        <span className="text-sm text-[#111827]">{item.label}</span>
                      </td>
                      <td className="px-6 py-4 text-right">
                        <select
                          value={contentSensitivity[item.key as keyof typeof contentSensitivity]}
                          onChange={(e) => handleSensitivityChange(item.key, e.target.value)}
                          className="h-9 px-3 text-sm border border-[#E5E7EB] rounded focus:outline-none focus:border-[#DC2626] focus:ring-1 focus:ring-[#DC2626]"
                        >
                          <option value="none">None</option>
                          <option value="mild">Mild</option>
                          <option value="strong">Strong</option>
                        </select>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </Card>

        {/* D. Subtitles & Accessibility */}
        <Card padding="none">
          <div className="p-6 border-b border-[#E5E7EB]">
            <h2 className="text-lg font-semibold text-[#111827] mb-1">Subtitles & Accessibility</h2>
            <p className="text-sm text-[#6B7280]">Ensure your content is accessible to all audiences</p>
          </div>
          <div className="p-6">
            <div className="space-y-6">
              {/* Auto-generate subtitles */}
              <div className="flex items-center justify-between p-4 border border-[#E5E7EB] rounded-lg">
                <div className="flex items-center gap-3">
                  <Subtitles size={20} className="text-[#6B7280]" />
                  <div>
                    <div className="font-medium text-[#111827] mb-1">Auto-generate subtitles</div>
                    <div className="text-sm text-[#6B7280]">Automatically create captions for video content</div>
                  </div>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={subtitlesEnabled}
                    onChange={(e) => setSubtitlesEnabled(e.target.checked)}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-red-300 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#DC2626]"></div>
                </label>
              </div>

              {/* Upload SRT/VTT */}
              <div>
                <label className="block text-sm font-medium text-[#111827] mb-2">Upload Caption File</label>
                <div className="flex items-center gap-3">
                  <Button variant="secondary" size="md">
                    <FileText size={16} className="mr-2" />
                    Upload SRT / VTT
                  </Button>
                  <span className="text-sm text-[#6B7280]">No file selected</span>
                </div>
              </div>

              {/* Language Selector */}
              <div>
                <label className="block text-sm font-medium text-[#111827] mb-2">Caption Language</label>
                <select className="w-full max-w-xs h-10 px-3 text-sm border border-[#E5E7EB] rounded focus:outline-none focus:border-[#DC2626] focus:ring-1 focus:ring-[#DC2626]">
                  <option>English (US)</option>
                  <option>Spanish</option>
                  <option>French</option>
                  <option>German</option>
                  <option>Japanese</option>
                  <option>Chinese (Simplified)</option>
                </select>
              </div>

              {/* ALT Text */}
              <div>
                <label className="block text-sm font-medium text-[#111827] mb-2">ALT Text for Images</label>
                <textarea
                  placeholder="Describe your images for screen readers and accessibility..."
                  className="w-full h-24 p-3 text-sm border border-[#E5E7EB] rounded focus:outline-none focus:border-[#DC2626] focus:ring-1 focus:ring-[#DC2626] resize-none"
                />
              </div>

              {/* Accessibility Checklist */}
              <div>
                <div className="text-sm font-medium text-[#111827] mb-3">Accessibility Checklist</div>
                <div className="space-y-2">
                  <label className="flex items-center gap-3 p-3 bg-[#F9FAFB] rounded">
                    <input type="checkbox" className="w-4 h-4" />
                    <span className="text-sm text-[#111827]">Clear caption or title provided</span>
                  </label>
                  <label className="flex items-center gap-3 p-3 bg-[#F9FAFB] rounded">
                    <input type="checkbox" className="w-4 h-4" />
                    <span className="text-sm text-[#111827]">No rapid flashing or strobing visuals</span>
                  </label>
                  <label className="flex items-center gap-3 p-3 bg-[#F9FAFB] rounded">
                    <input type="checkbox" className="w-4 h-4" />
                    <span className="text-sm text-[#111827]">Proper description for visual elements</span>
                  </label>
                </div>
              </div>
            </div>
          </div>
        </Card>

        {/* E. Copyright & Platform Rules Scan */}
        <Card padding="none">
          <div className="p-6 border-b border-[#E5E7EB]">
            <h2 className="text-lg font-semibold text-[#111827] mb-1">Copyright & Platform Rules Scan</h2>
            <p className="text-sm text-[#6B7280]">Automated check for copyright and policy violations</p>
          </div>
          <div className="p-6">
            {scanningStatus === 'scanning' ? (
              <div className="py-8 text-center">
                <div className="w-16 h-16 mx-auto mb-4 border-4 border-[#E5E7EB] border-t-[#DC2626] rounded-full animate-spin"></div>
                <div className="text-sm font-medium text-[#111827] mb-2">Scanning for copyright issues...</div>
                <div className="text-sm text-[#6B7280]">This may take a few moments</div>
              </div>
            ) : (
              <div className="space-y-4">
                {/* Scan Results Summary */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {/* Audio Check */}
                  <div className="p-4 border border-[#E5E7EB] rounded-lg">
                    <div className="flex items-center gap-3 mb-3">
                      <Music size={20} className="text-[#6B7280]" />
                      <span className="font-medium text-[#111827]">Audio</span>
                    </div>
                    <div className="flex items-center gap-2 mb-2">
                      {getStatusIcon(copyrightScanResults.audio.status)}
                      <span className="text-sm text-[#111827]">{copyrightScanResults.audio.message}</span>
                    </div>
                    <button className="text-sm text-[#DC2626] hover:underline">View details</button>
                  </div>

                  {/* Visual Content Check */}
                  <div className="p-4 border border-[#E5E7EB] rounded-lg">
                    <div className="flex items-center gap-3 mb-3">
                      <Eye size={20} className="text-[#6B7280]" />
                      <span className="font-medium text-[#111827]">Visual Content</span>
                    </div>
                    <div className="flex items-center gap-2 mb-2">
                      {getStatusIcon(copyrightScanResults.visual.status)}
                      <span className="text-sm text-[#111827]">{copyrightScanResults.visual.message}</span>
                    </div>
                    <button className="text-sm text-[#DC2626] hover:underline">View details</button>
                  </div>

                  {/* Text/Caption Check */}
                  <div className="p-4 border border-[#E5E7EB] rounded-lg">
                    <div className="flex items-center gap-3 mb-3">
                      <FileText size={20} className="text-[#6B7280]" />
                      <span className="font-medium text-[#111827]">Text/Caption</span>
                    </div>
                    <div className="flex items-center gap-2 mb-2">
                      {getStatusIcon(copyrightScanResults.text.status)}
                      <span className="text-sm text-[#111827]">{copyrightScanResults.text.message}</span>
                    </div>
                    <button className="text-sm text-[#DC2626] hover:underline">View details</button>
                  </div>
                </div>

                {/* Warning Notice */}
                {copyrightScanResults.text.status === 'warning' && (
                  <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                    <div className="flex items-start gap-3">
                      <AlertTriangle size={20} className="text-yellow-600 flex-shrink-0 mt-0.5" />
                      <div>
                        <div className="font-medium text-yellow-900 mb-1">Brand mention detected</div>
                        <div className="text-sm text-yellow-800">
                          Your content mentions a trademarked brand. This may result in limited visibility on some
                          platforms. Review your content to ensure compliance with brand guidelines.
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </Card>

        {/* F. Final Review Summary */}
        <Card padding="none">
          <div className="p-6 border-b border-[#E5E7EB]">
            <div className="flex items-center gap-3">
              <Shield size={24} className="text-[#DC2626]" />
              <h2 className="text-lg font-semibold text-[#111827]">Final Review Summary</h2>
            </div>
          </div>
          <div className="p-6">
            <div className="space-y-6">
              {/* Summary Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Age Rating */}
                <div className="p-4 bg-[#F9FAFB] border border-[#E5E7EB] rounded-lg">
                  <div className="text-sm font-medium text-[#6B7280] mb-2">Age Rating</div>
                  <div className="text-base font-semibold text-[#111827]">
                    {ageRestriction === 'all-audiences' && 'Suitable for all audiences'}
                    {ageRestriction === 'not-for-kids' && 'Not made for kids'}
                    {ageRestriction === '18-plus' && '18+ restricted content'}
                    {ageRestriction === 'sensitive' && 'Contains sensitive themes'}
                  </div>
                </div>

                {/* Audio License */}
                <div className="p-4 bg-[#F9FAFB] border border-[#E5E7EB] rounded-lg">
                  <div className="text-sm font-medium text-[#6B7280] mb-2">Audio License Status</div>
                  <div className="flex items-center gap-2">
                    {getStatusIcon(detectedAudio.status)}
                    <span className="text-base font-semibold text-[#111827]">
                      {detectedAudio.status === 'licensed' ? 'Licensed' : 'Unlicensed'}
                    </span>
                  </div>
                </div>

                {/* Sensitive Content */}
                <div className="p-4 bg-[#F9FAFB] border border-[#E5E7EB] rounded-lg">
                  <div className="text-sm font-medium text-[#6B7280] mb-2">Sensitive Content Rating</div>
                  <div className="text-base font-semibold text-[#111827]">
                    {Object.values(contentSensitivity).some((v) => v !== 'none')
                      ? 'Flagged content present'
                      : 'No sensitive content'}
                  </div>
                </div>

                {/* Subtitles */}
                <div className="p-4 bg-[#F9FAFB] border border-[#E5E7EB] rounded-lg">
                  <div className="text-sm font-medium text-[#6B7280] mb-2">Subtitle Availability</div>
                  <div className="flex items-center gap-2">
                    {subtitlesEnabled ? (
                      <>
                        <CheckCircle size={18} className="text-green-600" />
                        <span className="text-base font-semibold text-[#111827]">Enabled</span>
                      </>
                    ) : (
                      <>
                        <XCircle size={18} className="text-gray-400" />
                        <span className="text-base font-semibold text-[#111827]">Not enabled</span>
                      </>
                    )}
                  </div>
                </div>

                {/* Copyright Check */}
                <div className="p-4 bg-[#F9FAFB] border border-[#E5E7EB] rounded-lg md:col-span-2">
                  <div className="text-sm font-medium text-[#6B7280] mb-2">Copyright Check Result</div>
                  <div className="flex items-center gap-4">
                    <div className="flex items-center gap-2">
                      {getStatusIcon('pass')}
                      <span className="text-sm text-[#111827]">Audio: Clear</span>
                    </div>
                    <div className="flex items-center gap-2">
                      {getStatusIcon('pass')}
                      <span className="text-sm text-[#111827]">Visual: Clear</span>
                    </div>
                    <div className="flex items-center gap-2">
                      {getStatusIcon('warning')}
                      <span className="text-sm text-[#111827]">Text: Review needed</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Platform Warnings */}
              <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <div className="flex items-start gap-3">
                  <AlertCircle size={20} className="text-blue-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <div className="font-medium text-blue-900 mb-2">Platform-Specific Warnings</div>
                    <ul className="space-y-1 text-sm text-blue-800">
                      <li>
                        • <strong>Instagram Reels:</strong> Copyrighted audio may result in muted playback
                      </li>
                      <li>
                        • <strong>YouTube:</strong> Brand mentions require proper disclosure if sponsored
                      </li>
                      <li>
                        • <strong>TikTok:</strong> Age-restricted content will have limited reach
                      </li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex items-center justify-end gap-3 pt-4 border-t border-[#E5E7EB]">
                <Button variant="secondary" size="lg" onClick={handleFixIssues}>
                  <AlertTriangle size={18} className="mr-2" />
                  Fix Issues
                </Button>
                <Button variant="primary" size="lg" onClick={handleContinueToSchedule}>
                  <CheckCircle size={18} className="mr-2" />
                  Continue to Schedule
                </Button>
              </div>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
}
